import { useEffect, useState, useRef } from "react"
import { useTranslation } from "react-i18next"
import ReactMarkdown from "react-markdown"
import rehypeSlug from "rehype-slug"
import remarkGfm from "remark-gfm"

import { useAiReportDetailQuery } from "@/hooks/api/useAiReportQuery"

import "./aiReportDialog.scss"

/**
 * 파서가 놓친 **text** 패턴을 <strong>으로 변환하는 rehype 플러그인.
 * CommonMark emphasis 규칙상 한글 뒤에 **-3.8%** 같은 패턴이 오면
 * 볼드 처리가 안 되는데, 이때 텍스트 노드에 **가 그대로 남게 됨.
 * 이 플러그인은 그런 케이스만 잡아서 strong 엘리먼트로 변환해줌.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function rehypeBoldFallback() {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (tree: any) => {
    walk(tree)
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function walk(node: any) {
  if (!node.children) return

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const newChildren: any[] = []
  let changed = false

  for (const child of node.children) {
    if (child.type === "text" && /\*\*[^*\n]+?\*\*/.test(child.value)) {
      const parts = parseBoldInText(child.value)
      newChildren.push(...parts)
      changed = true
    } else {
      if (child.children) walk(child)
      newChildren.push(child)
    }
  }

  if (changed) node.children = newChildren
}

function parseBoldInText(text: string) {
  const regex = /\*\*([^*\n]+?)\*\*/g
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const result: any[] = []
  let last = 0
  let m

  while ((m = regex.exec(text))) {
    if (m.index > last) {
      result.push({ type: "text", value: text.slice(last, m.index) })
    }
    result.push({
      type: "element",
      tagName: "strong",
      properties: {},
      children: [{ type: "text", value: m[1] }],
    })
    last = m.index + m[0].length
  }

  if (last < text.length) {
    result.push({ type: "text", value: text.slice(last) })
  }

  return result
}

type Heading = {
  level: number
  text: string
  id: string
}

type AIReportDialogProps = {
  open: boolean
  onClose: () => void
  idfReport: number
}

export default function AIReportDialog({ open, onClose, idfReport }: AIReportDialogProps) {
  const { t } = useTranslation()
  const contentRef = useRef<HTMLDivElement>(null)
  const [headings, setHeadings] = useState<Heading[]>([])
  const [isMobileTocOpen, setIsMobileTocOpen] = useState(false)

  const { data: report, isLoading } = useAiReportDetailQuery(idfReport)

  useEffect(() => {
    if (open && contentRef.current && report?.result) {
      const timer = setTimeout(() => {
        const headingElements = contentRef.current?.querySelectorAll("h1, h2, h3, h4, h5, h6")
        const extractedHeadings = Array.from(headingElements || []).map((el) => ({
          level: parseInt(el.tagName.charAt(1)),
          text: el.textContent || "",
          id: el.id,
        }))
        setHeadings(extractedHeadings)
      }, 100)

      return () => clearTimeout(timer)
    }
  }, [open, report?.result])

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && open) {
        onClose()
      }
    }

    if (open) {
      document.addEventListener("keydown", handleEscape)
      document.body.style.overflow = "hidden"
    }

    return () => {
      document.removeEventListener("keydown", handleEscape)
      document.body.style.overflow = "unset"
    }
  }, [open, onClose])

  if (!open || !idfReport) return null

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  const handleTocClick = (e: React.MouseEvent, id: string) => {
    e.preventDefault()
    const element = document.getElementById(id)
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" })
      setIsMobileTocOpen(false)
    }
  }

  return (
    <div className="detail-report-overlay" onClick={handleOverlayClick}>
      <div className="detail-report-dialog">
        <div className="dialog-header">
          <div className="dialog-title">
            <h2>{report?.title || t("loading")}</h2>
          </div>
          <button className="close-button" onClick={onClose} aria-label={t("button.close")}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M18 6L6 18M6 6l12 12"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
        <div className="dialog-body">
          {headings.length > 0 && (
            <aside className="toc-sidebar">
              <div className="toc-title">{t("tableOfContents") || "Table of Contents"}</div>
              <nav className="toc-nav">
                {headings.map((heading, index) => (
                  <a
                    key={index}
                    href={`#${heading.id}`}
                    className={`toc-link toc-level-${heading.level}`}
                    onClick={(e) => handleTocClick(e, heading.id)}>
                    {heading.text}
                  </a>
                ))}
              </nav>
            </aside>
          )}
          <div className="dialog-content" ref={contentRef}>
            {isLoading ? (
              <div className="loading-state">{t("loading")}</div>
            ) : (
              <>
                {headings.length > 0 && (
                  <div className="mobile-toc">
                    <button
                      className="mobile-toc-toggle"
                      onClick={() => setIsMobileTocOpen(!isMobileTocOpen)}>
                      <span>{t("tableOfContents") || "Table of Contents"}</span>
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        style={{
                          transform: isMobileTocOpen ? "rotate(180deg)" : "rotate(0)",
                          transition: "transform 0.2s",
                        }}>
                        <path
                          d="M4 6L8 10L12 6"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                    {isMobileTocOpen && (
                      <nav className="mobile-toc-nav">
                        {headings.map((heading, index) => (
                          <a
                            key={index}
                            href={`#${heading.id}`}
                            className={`mobile-toc-link mobile-toc-level-${heading.level}`}
                            onClick={(e) => handleTocClick(e, heading.id)}>
                            {heading.text}
                          </a>
                        ))}
                      </nav>
                    )}
                  </div>
                )}
                <ReactMarkdown
                  remarkPlugins={[[remarkGfm, { singleTilde: false }]]}
                  rehypePlugins={[rehypeSlug, rehypeBoldFallback]}>
                  {report?.result || ""}
                </ReactMarkdown>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
